import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api, empresasApi, getDisplayMessage, setToken } from '@/api/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useQuery } from '@tanstack/react-query';

function parsePrecoMensalQuery(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  const n = parseFloat(String(raw).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** API pública usa `preco_mensal`; alguns ambientes podem expor `precoMensal`. */
function precoMensalDoPlano(p) {
  if (p == null) return NaN;
  const v = p.preco_mensal ?? p.precoMensal;
  return Number(v);
}

function planoIdPorPreco(planos, preco) {
  if (!Array.isArray(planos) || preco == null) return '';
  const matches = planos
    .map((p, i) => ({ p, i }))
    .filter(({ p }) => {
      const pm = precoMensalDoPlano(p);
      return Number.isFinite(pm) && Math.abs(pm - preco) < 0.01;
    });
  if (matches.length === 0) return '';
  matches.sort((a, b) => a.i - b.i);
  return matches[0].p.id;
}

/** Igual à landing (`docs/index.html`): slug do nome (Start, Profissional, Empresarial…). */
function planSlugFromNome(nome) {
  return String(nome || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '');
}

/** Landing usa `plano=start`; no SuperAdmin o plano pode chamar-se "Starter" (`starter`). */
function slugPlanoEquivale(slugA, slugB) {
  const a = String(slugA || '').trim();
  const b = String(slugB || '').trim();
  if (!a || !b) return false;
  if (a === b) return true;
  const startish = new Set(['start', 'starter']);
  return startish.has(a) && startish.has(b);
}

function planoIdPorSlug(planos, slugOuNome) {
  if (!Array.isArray(planos) || slugOuNome == null || String(slugOuNome).trim() === '') return '';
  const target = planSlugFromNome(slugOuNome);
  if (!target) return '';
  const p = planos.find((x) => slugPlanoEquivale(target, planSlugFromNome(x.nome)));
  return p?.id ?? '';
}

function parsePlanoSlugFromSearchParams(searchParams) {
  const raw = (searchParams.get('plano') ?? '').trim();
  if (!raw) return null;
  return planSlugFromNome(raw);
}

function normNomePlano(nome) {
  return String(nome || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/** Cadastro manual (sem URL): Start/Starter ou primeiro plano da API. */
function findPlanoStartOuPrimeiro(planos) {
  if (!Array.isArray(planos) || planos.length === 0) return null;
  const start = planos.find((p) => {
    const n = normNomePlano(p.nome);
    return n === 'start' || n === 'starter';
  });
  return start ?? planos[0];
}

/** Trial 14d: igual ao backend sem env — só plano com grátis+14 no nome (não é Start/Starter). */
function findPlanoTrialHeuristica(planos) {
  if (!Array.isArray(planos) || planos.length === 0) return null;
  const gratis14 = planos.find((p) => {
    const n = normNomePlano(p.nome);
    return n.includes('gratis') && n.includes('14');
  });
  return gratis14 ?? null;
}

/** Trial na URL: ?trial=1, ?trial=true ou só ?trial (flag). */
function parseTrialFromSearchParams(searchParams) {
  if (!searchParams.has('trial')) return false;
  const raw = (searchParams.get('trial') ?? '').trim().toLowerCase();
  if (raw === '' || raw === '1' || raw === 'true' || raw === 'yes' || raw === 'sim') return true;
  if (raw === '0' || raw === 'false' || raw === 'no' || raw === 'nao') return false;
  return true;
}

export default function RegistroPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const trialParam = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return parseTrialFromSearchParams(sp);
  }, [location.search]);
  const precoParam = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return parsePrecoMensalQuery(sp.get('preco_mensal'));
  }, [location.search]);
  const planoSlugParam = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return parsePlanoSlugFromSearchParams(sp);
  }, [location.search]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sucesso, setSucesso] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepLookupError, setCepLookupError] = useState('');
  const [form, setForm] = useState({
    razao_social: '',
    nome_fantasia: '',
    cnpj: '',
    email_principal: '',
    telefone: '',
    cep: '',
    logradouro: '',
    numero: '',
    bairro: '',
    cidade: '',
    uf: '',
    plano_id: '',
    usuario_admin: {
      nome: '',
      email: '',
      senha: '',
    },
  });
  const formCepRef = useRef(form.cep);
  formCepRef.current = form.cep;

  const { data: planos = [], isLoading: planosCarregando } = useQuery({
    queryKey: ['planos-public'],
    queryFn: () => api.get('/planos').catch(() => []),
  });

  const intencao = useMemo(() => {
    if (trialParam) return 'trial_14d';
    if (precoParam != null || planoSlugParam != null) return 'contratar_plano';
    return undefined;
  }, [trialParam, precoParam, planoSlugParam]);

  const { planoResolvido, contratarErroUrl } = useMemo(() => {
    if (!Array.isArray(planos) || planos.length === 0) {
      return { planoResolvido: null, contratarErroUrl: null };
    }
    if (trialParam) {
      return { planoResolvido: findPlanoTrialHeuristica(planos), contratarErroUrl: null };
    }
    if (precoParam != null && planoSlugParam) {
      const idSlug = planoIdPorSlug(planos, planoSlugParam);
      const idPreco = planoIdPorPreco(planos, precoParam);
      if (!idSlug) return { planoResolvido: null, contratarErroUrl: 'slug' };
      // `plano=` é a fonte de verdade: links antigos / cache com preco_mensal errado (ex.: 99) ainda funcionam
      if (!idPreco || idPreco !== idSlug) {
        return { planoResolvido: planos.find((p) => p.id === idSlug) ?? null, contratarErroUrl: null };
      }
      return { planoResolvido: planos.find((p) => p.id === idPreco) ?? null, contratarErroUrl: null };
    }
    if (precoParam != null) {
      const id = planoIdPorPreco(planos, precoParam);
      return {
        planoResolvido: id ? planos.find((p) => p.id === id) ?? null : null,
        contratarErroUrl: id ? null : 'preco',
      };
    }
    if (planoSlugParam) {
      const id = planoIdPorSlug(planos, planoSlugParam);
      return {
        planoResolvido: id ? planos.find((p) => p.id === id) ?? null : null,
        contratarErroUrl: id ? null : 'slug',
      };
    }
    return { planoResolvido: findPlanoStartOuPrimeiro(planos), contratarErroUrl: null };
  }, [planos, trialParam, precoParam, planoSlugParam]);

  const precoExibicao = useMemo(() => {
    if (intencao === 'contratar_plano' && planoResolvido) {
      const v = precoMensalDoPlano(planoResolvido);
      if (Number.isFinite(v)) return v;
    }
    if (precoParam != null) return precoParam;
    return null;
  }, [precoParam, intencao, planoResolvido]);

  /** Corrige a URL quando preco_mensal está obsoleto mas plano= resolve (evita confusão e links antigos). */
  useEffect(() => {
    if (trialParam || intencao !== 'contratar_plano' || !planoResolvido) return;
    const canon = precoMensalDoPlano(planoResolvido);
    if (!Number.isFinite(canon)) return;
    if (precoParam == null || Math.abs(precoParam - canon) < 0.02) return;
    const sp = new URLSearchParams(location.search);
    sp.set('preco_mensal', String(canon));
    if (planoSlugParam) sp.set('plano', planoSlugParam);
    navigate({ pathname: location.pathname, search: `?${sp.toString()}` }, { replace: true });
  }, [
    trialParam,
    intencao,
    planoResolvido,
    precoParam,
    planoSlugParam,
    location.pathname,
    location.search,
    navigate,
  ]);

  useEffect(() => {
    if (!planoResolvido?.id) return;
    setForm((prev) => (prev.plano_id === planoResolvido.id ? prev : { ...prev, plano_id: planoResolvido.id }));
  }, [planoResolvido?.id]);

  useEffect(() => {
    if (intencao !== 'contratar_plano') {
      setCepLookupError('');
      setCepLoading(false);
      return undefined;
    }
    const requested = String(form.cep ?? '').replace(/\D/g, '');
    if (requested.length !== 8) {
      setCepLookupError('');
      setCepLoading(false);
      return undefined;
    }
    const ac = new AbortController();
    setCepLoading(true);
    setCepLookupError('');
    (async () => {
      try {
        const res = await fetch(`https://viacep.com.br/ws/${requested}/json/`, { signal: ac.signal });
        if (!res.ok) throw new Error('http');
        const data = await res.json();
        if (ac.signal.aborted) return;
        const cepAgora = String(formCepRef.current ?? '').replace(/\D/g, '');
        if (cepAgora !== requested) return;
        if (data?.erro) {
          setCepLookupError('CEP não encontrado. Confira os dígitos ou preencha o endereço manualmente.');
          return;
        }
        setCepLookupError('');
        setForm((prev) => ({
          ...prev,
          logradouro: typeof data.logradouro === 'string' ? data.logradouro : '',
          bairro: typeof data.bairro === 'string' ? data.bairro : '',
          cidade: typeof data.localidade === 'string' ? data.localidade : '',
          uf:
            typeof data.uf === 'string'
              ? data.uf.replace(/\s/g, '').slice(0, 2).toUpperCase()
              : '',
        }));
      } catch (e) {
        if (ac.signal.aborted) return;
        const cepAgora = String(formCepRef.current ?? '').replace(/\D/g, '');
        if (cepAgora !== requested) return;
        setCepLookupError('Não foi possível consultar o CEP. Preencha o endereço manualmente.');
      } finally {
        if (!ac.signal.aborted) {
          setCepLoading(false);
        }
      }
    })();
    return () => ac.abort();
  }, [form.cep, intencao]);

  const handleChange = (field, value) => {
    if (field.startsWith('usuario_admin.')) {
      const key = field.split('.')[1];
      setForm((prev) => ({
        ...prev,
        usuario_admin: { ...prev.usuario_admin, [key]: value },
      }));
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      let planoId = form.plano_id;
      if (intencao === 'trial_14d') {
        if (!planoResolvido?.id) {
          setError(
            'Não há plano de trial disponível. Crie um plano ativo com "Grátis" e "14" no nome (ex.: Grátis 14 dias) — o trial não usa o plano Start. Em alternativa, o administrador pode definir REGISTRO_TRIAL_PLANO_ID na API.'
          );
          setLoading(false);
          return;
        }
        planoId = planoResolvido.id;
      } else if (intencao === 'contratar_plano') {
        const telDigits = String(form.telefone ?? '').replace(/\D/g, '');
        const cepDigits = String(form.cep ?? '').replace(/\D/g, '');
        if (telDigits.length < 10) {
          setError('Informe o telefone da empresa com DDD (mínimo 10 dígitos) para seguir para o pagamento.');
          setLoading(false);
          return;
        }
        if (cepDigits.length !== 8) {
          setError('Informe o CEP da sede com 8 dígitos.');
          setLoading(false);
          return;
        }
        if (!String(form.logradouro ?? '').trim() || !String(form.numero ?? '').trim() || !String(form.bairro ?? '').trim()) {
          setError('Preencha logradouro, número e bairro da sede da empresa.');
          setLoading(false);
          return;
        }
        const ufTrim = String(form.uf ?? '').trim().toUpperCase();
        if (!String(form.cidade ?? '').trim() || ufTrim.length !== 2) {
          setError('Preencha cidade e UF (estado) da sede da empresa. A UF deve ter 2 letras.');
          setLoading(false);
          return;
        }
        const idPreco = precoParam != null ? planoIdPorPreco(planos, precoParam) : '';
        const idSlug = planoSlugParam ? planoIdPorSlug(planos, planoSlugParam) : '';

        if (planoSlugParam && precoParam != null) {
          if (!idSlug) {
            setError(
              'Não há plano ativo para este identificador na URL (ex.: plano=start, plano=profissional). Confira o nome do plano no SuperAdmin.'
            );
            setLoading(false);
            return;
          }
          planoId = idSlug;
        } else if (precoParam != null) {
          if (!idPreco) {
            setError(
              'Não há plano ativo com o preço indicado na URL. O valor pode ter sido atualizado — use o link atual do site ou o cadastro sem o parâmetro preco_mensal.'
            );
            setLoading(false);
            return;
          }
          planoId = idPreco;
        } else if (planoSlugParam) {
          if (!idSlug) {
            setError(
              'Não há plano ativo para este identificador (ex.: profissional, empresarial). Confira o nome do plano no SuperAdmin.'
            );
            setLoading(false);
            return;
          }
          planoId = idSlug;
        }
      } else {
        if (!planoId && planoResolvido?.id) planoId = planoResolvido.id;
        if (!planoId && planos.length > 0) planoId = planos[0].id;
      }
      const planoPayload = intencao === 'contratar_plano' && planoId ? planos.find((p) => p.id === planoId) : null;
      const precoMensalApi =
        intencao === 'contratar_plano' && planoPayload ? precoMensalDoPlano(planoPayload) : NaN;
      const precoMensalApiFinal = Number.isFinite(precoMensalApi) ? precoMensalApi : null;
      const payload = {
        ...form,
        uf: String(form.uf ?? '')
          .trim()
          .toUpperCase()
          .slice(0, 2),
        plano_id: planoId,
        intencao,
        ...(intencao === 'contratar_plano' && precoMensalApiFinal != null ? { preco_mensal: precoMensalApiFinal } : {}),
        ...(intencao === 'contratar_plano' && planoSlugParam ? { plano_slug: planoSlugParam } : {}),
      };
      const data = await empresasApi.registro(payload);
      if (data?.token) {
        setToken(data.token);
        if (intencao === 'contratar_plano' && (precoParam != null || planoSlugParam)) {
          const q = new URLSearchParams();
          if (precoMensalApiFinal != null) q.set('preco_mensal', String(precoMensalApiFinal));
          if (planoSlugParam) q.set('plano', planoSlugParam);
          window.location.assign(`/assinaturaplano?${q.toString()}`);
          return;
        }
        window.location.assign('/Dashboard');
        return;
      }
      setSucesso(true);
    } catch (err) {
      setError(getDisplayMessage(err, 'Erro ao cadastrar'));
    } finally {
      setLoading(false);
    }
  };

  if (sucesso) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle>Cadastro recebido</CardTitle>
            <CardDescription>
              Seu cadastro está em avaliação. Enviamos um e-mail para o administrador informando os próximos passos.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertDescription>
                Assim que o acesso for liberado pela equipe, você receberá outro e-mail e poderá entrar na plataforma.
                O período de trial de 14 dias começa após essa liberação.
              </AlertDescription>
            </Alert>
            <Button asChild className="w-full">
              <Link to="/Login">Ir para o login</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>Cadastrar empresa</CardTitle>
          <CardDescription>
            {trialParam && 'Trial de 14 dias — após cadastrar você entra direto na plataforma.'}
            {!trialParam &&
              intencao === 'contratar_plano' &&
              planoResolvido &&
              precoExibicao != null &&
              `Plano: ${planoResolvido.nome} — R$ ${precoExibicao.toFixed(2)}/mês — após cadastrar você será direcionado ao pagamento.`}
            {!trialParam && intencao === 'contratar_plano' && planosCarregando && 'Carregando planos…'}
            {!trialParam && intencao !== 'contratar_plano' && 'Preencha os dados da empresa e do administrador'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Razão social *</Label>
                <Input
                  value={form.razao_social}
                  onChange={(e) => handleChange('razao_social', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Nome fantasia</Label>
                <Input
                  value={form.nome_fantasia}
                  onChange={(e) => handleChange('nome_fantasia', e.target.value)}
                />
              </div>
              <div>
                <Label>CNPJ *</Label>
                <Input
                  value={form.cnpj}
                  onChange={(e) => handleChange('cnpj', e.target.value.replace(/\D/g, '').slice(0, 14))}
                  placeholder="Somente números"
                  maxLength={14}
                  required
                />
              </div>
              <div className="col-span-2">
                <Label>E-mail da empresa *</Label>
                <Input
                  type="email"
                  value={form.email_principal}
                  onChange={(e) => handleChange('email_principal', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Telefone{intencao === 'contratar_plano' ? ' *' : ''}</Label>
                <Input
                  value={form.telefone}
                  onChange={(e) => handleChange('telefone', e.target.value)}
                  placeholder="DDD + número"
                  required={intencao === 'contratar_plano'}
                />
              </div>
              {intencao === 'contratar_plano' && (
                <>
                  <div className="space-y-1">
                    <Label>CEP da sede *</Label>
                    <Input
                      value={form.cep}
                      onChange={(e) => handleChange('cep', e.target.value.replace(/\D/g, '').slice(0, 8))}
                      placeholder="Somente números"
                      maxLength={8}
                      required
                      disabled={cepLoading}
                      aria-busy={cepLoading}
                    />
                    {cepLoading && (
                      <p className="text-xs text-muted-foreground">Buscando endereço…</p>
                    )}
                    {!cepLoading && cepLookupError && (
                      <p className="text-xs text-destructive">{cepLookupError}</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <Label>Logradouro *</Label>
                    <Input
                      value={form.logradouro}
                      onChange={(e) => handleChange('logradouro', e.target.value)}
                      placeholder="Rua, avenida…"
                      required
                    />
                  </div>
                  <div>
                    <Label>Número *</Label>
                    <Input
                      value={form.numero}
                      onChange={(e) => handleChange('numero', e.target.value)}
                      placeholder="Ex.: 100 ou S/N"
                      required
                    />
                  </div>
                  <div>
                    <Label>Bairro *</Label>
                    <Input
                      value={form.bairro}
                      onChange={(e) => handleChange('bairro', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label>Cidade *</Label>
                    <Input
                      value={form.cidade}
                      onChange={(e) => handleChange('cidade', e.target.value)}
                      placeholder="Município"
                      required
                    />
                  </div>
                  <div>
                    <Label>UF (estado) *</Label>
                    <Input
                      value={form.uf}
                      onChange={(e) =>
                        handleChange('uf', e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 2))
                      }
                      placeholder="Ex.: PE"
                      maxLength={2}
                      required
                    />
                  </div>
                </>
              )}
              <div className="col-span-2 rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                {trialParam && planoResolvido && (
                  <>
                    Plano: <span className="font-medium text-foreground">{planoResolvido.nome}</span> — trial de 14
                    dias).
                  </>
                )}
                {trialParam && !planoResolvido && (
                  <span className="text-destructive">
                    Nenhum plano ativo encontrado com &quot;Grátis&quot; e &quot;14&quot; no nome. Ajuste o nome do
                    plano de trial no SuperAdmin ou defina REGISTRO_TRIAL_PLANO_ID na API.
                  </span>
                )}
                {!trialParam && intencao === 'contratar_plano' && planoResolvido && precoExibicao != null && (
                  <>
                    Plano: <span className="font-medium text-foreground">{planoResolvido.nome}</span> — R${' '}
                    {precoExibicao.toFixed(2)}
                    /mês. A lista completa de planos fica no painel do super administrador.
                  </>
                )}
                {!trialParam && intencao === 'contratar_plano' && !planoResolvido && contratarErroUrl === 'preco' && (
                  <span className="text-destructive">
                    Nenhum plano ativo corresponde a este preço. Atualize o link ou cadastre-se sem preco_mensal na URL
                    para análise da equipe.
                  </span>
                )}
                {!trialParam && intencao === 'contratar_plano' && !planoResolvido && contratarErroUrl === 'slug' && (
                  <span className="text-destructive">
                    Não há plano ativo para este identificador na URL (ex.: plano=profissional ou plano=empresarial).
                    Confira se o nome do plano no SuperAdmin gera o mesmo slug da landing.
                  </span>
                )}
                {!trialParam && intencao !== 'contratar_plano' && (
                  <>
                    Após o cadastro, a equipe analisa a solicitação e define o plano comercial. A escolha entre todos os
                    planos do catálogo é feita apenas no painel do super administrador.
                  </>
                )}
              </div>
            </div>
            <hr />
            <p className="font-medium">Administrador</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nome *</Label>
                <Input
                  value={form.usuario_admin.nome}
                  onChange={(e) => handleChange('usuario_admin.nome', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>E-mail *</Label>
                <Input
                  type="email"
                  value={form.usuario_admin.email}
                  onChange={(e) => handleChange('usuario_admin.email', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Senha *</Label>
                <Input
                  type="password"
                  value={form.usuario_admin.senha}
                  onChange={(e) => handleChange('usuario_admin.senha', e.target.value)}
                  required
                  minLength={6}
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={
                loading ||
                cepLoading ||
                (trialParam && !planoResolvido?.id) ||
                (intencao === 'contratar_plano' && !planoResolvido?.id)
              }
            >
              {loading ? 'Cadastrando...' : 'Cadastrar'}
            </Button>
          </form>
          <p className="mt-4 text-center text-sm text-slate-600">
            Já tem conta?{' '}
            <Link to="/Login" className="text-blue-600 hover:underline">
              Entrar
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
